import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Linkedin, CheckCircle2, XCircle, Loader2, RefreshCw, Unlink, MonitorPlay, Maximize2, Minimize2, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

function wsUrl(action) {
  const base = API.replace(/^http/, 'ws');
  return `${base}/api/linkedin/ws/${action}`;
}

export default function LinkedInIntegrationCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [logs, setLogs] = useState([]);
  const [screenshot, setScreenshot] = useState(null);
  const [progress, setProgress] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const wsRef = useRef(null);
  const logEndRef = useRef(null);
  const imgRef = useRef(null);
  const previewRef = useRef(null);
  const connectingRef = useRef(false);
  const reconnectCountRef = useRef(0);
  const MAX_RECONNECTS = 15; // 15 × 60 s proxy-limit ≈ 15 min total

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/linkedin/status`, { credentials: 'include' });
      if (res.ok) setStatus(await res.json());
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [logs]);
  useEffect(() => { connectingRef.current = connecting; }, [connecting]);

  const handleWsMessage = useCallback((event) => {
    try {
      const msg = JSON.parse(event.data);
      switch (msg.type) {
        case 'log':
          setLogs(prev => [...prev, { text: msg.message, ts: Date.now() }]);
          break;
        case 'screenshot':
          setScreenshot(`data:image/jpeg;base64,${msg.data}`);
          break;
        case 'progress':
          setProgress({ current: msg.current, total: msg.total, label: msg.label || '' });
          break;
        case 'auth_success':
          toast.success('LinkedIn connected!');
          setConnecting(false);
          fetchStatus();
          break;
        case 'auth_failed':
          toast.error(`LinkedIn auth failed: ${msg.reason || 'unknown'}`);
          setConnecting(false);
          break;
        case 'sync_complete':
          toast.success(`Sync complete: ${msg.stats?.jobs_found || 0} jobs, ${msg.stats?.applicants_found || 0} applicants`);
          setSyncing(false);
          setProgress(null);
          fetchStatus();
          break;
        case 'done':
          setConnecting(false);
          setSyncing(false);
          break;
        case 'error':
          toast.error(msg.message || 'LinkedIn error');
          setConnecting(false);
          setSyncing(false);
          break;
        default:
          break;
      }
    } catch { /* ignore parse errors */ }
  }, [fetchStatus]);

  const connectWs = useCallback((action, clearState = true) => {
    if (wsRef.current) {
      try { wsRef.current.close(); } catch {}
    }
    if (clearState) {
      setLogs([]);
      setScreenshot(null);
      setProgress(null);
      reconnectCountRef.current = 0;
    }

    const ws = new WebSocket(wsUrl(action));
    let keepAlive = null;
    ws.onopen = () => {
      keepAlive = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 25000);
    };
    ws.onmessage = handleWsMessage;
    ws.onerror = () => {
      toast.error('WebSocket connection failed');
      setConnecting(false);
      setSyncing(false);
    };
    ws.onclose = () => {
      if (keepAlive) clearInterval(keepAlive);
      // Auto-reconnect on proxy timeout during auth
      if (action === 'auth' && connectingRef.current && reconnectCountRef.current < MAX_RECONNECTS) {
        reconnectCountRef.current++;
        setTimeout(() => connectWs('auth', false), 1000);
        return;
      }
      setConnecting(false);
      setSyncing(false);
    };
    wsRef.current = ws;
  }, [handleWsMessage]);

  const handleConnect = () => {
    setConnecting(true);
    connectWs('auth');
  };

  const handleSync = () => {
    setSyncing(true);
    connectWs('sync');
  };

  const handleDisconnect = async () => {
    try {
      await fetch(`${API}/api/linkedin/disconnect`, { method: 'POST', credentials: 'include' });
      toast.success('LinkedIn disconnected');
      fetchStatus();
    } catch { toast.error('Failed to disconnect'); }
  };

  // ── Interactive preview: click + keyboard forwarding ──
  const handlePreviewClick = useCallback((e) => {
    previewRef.current?.focus();
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const x = Math.round((e.clientX - rect.left) * (1280 / rect.width));
    const y = Math.round((e.clientY - rect.top) * (800 / rect.height));
    if (x >= 0 && y >= 0 && x <= 1280 && y <= 800) {
      ws.send(JSON.stringify({ type: 'click', x, y }));
    }
  }, []);

  const handlePreviewKeyDown = useCallback((e) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    e.preventDefault();
    e.stopPropagation();
    const ignore = ['Shift', 'Control', 'Alt', 'Meta', 'CapsLock'];
    if (ignore.includes(e.key)) return;
    const specials = ['Enter', 'Backspace', 'Tab', 'Escape', 'ArrowUp', 'ArrowDown',
      'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'];
    if (specials.includes(e.key)) {
      ws.send(JSON.stringify({ type: 'keypress', key: e.key }));
    } else if (e.key.length === 1) {
      ws.send(JSON.stringify({ type: 'type', text: e.key }));
    }
  }, []);

  // Auto-focus preview when screenshot arrives during auth
  useEffect(() => {
    if (screenshot && connecting && previewRef.current) {
      previewRef.current.focus();
    }
  }, [screenshot, connecting]);

  // Close lightbox when auth finishes
  useEffect(() => {
    if (!connecting) setExpanded(false);
  }, [connecting]);

  const isConnected = status?.connected || status?.session_exists;
  const lastSync = status?.last_sync_result;
  const isActive = connecting || syncing;

  return (
    <Card className="flex flex-col" data-testid="linkedin-integration-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#0A66C2] flex items-center justify-center">
              <Linkedin className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-sm">LinkedIn Recruiter</CardTitle>
              <CardDescription className="text-xs">Sync posted jobs and applicants</CardDescription>
            </div>
          </div>
          <Badge
            variant={isConnected ? "default" : "secondary"}
            className={isConnected ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border-0" : ""}
            data-testid="linkedin-status-badge"
          >
            {isConnected ? (
              <><CheckCircle2 className="w-3 h-3 mr-1" /> Connected</>
            ) : (
              <><XCircle className="w-3 h-3 mr-1" /> Not Connected</>
            )}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="flex-1 space-y-3 pt-0">
        {lastSync && (
          <div className="text-[10px] text-muted-foreground bg-muted/30 rounded px-2 py-1.5 border">
            Last sync: {lastSync.timestamp ? new Date(lastSync.timestamp).toLocaleString() : '—'} |
            Jobs: {lastSync.stats?.jobs_found ?? '?'} |
            Applicants: {lastSync.stats?.applicants_found ?? '?'}
            {lastSync.error && <span className="text-red-500 ml-1">Error: {lastSync.error}</span>}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          {!isConnected ? (
            <Button
              size="sm"
              onClick={handleConnect}
              disabled={isActive}
              data-testid="linkedin-connect-btn"
              className="bg-[#0A66C2] hover:bg-[#004182] text-white"
            >
              {connecting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Linkedin className="w-3.5 h-3.5 mr-1" />}
              {connecting ? 'Connecting...' : 'Connect LinkedIn'}
            </Button>
          ) : (
            <>
              <Button size="sm" onClick={handleSync} disabled={isActive} data-testid="linkedin-sync-btn">
                {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                {syncing ? 'Syncing...' : 'Sync Now'}
              </Button>
              <Button size="sm" variant="outline" onClick={handleDisconnect} disabled={isActive} data-testid="linkedin-disconnect-btn">
                <Unlink className="w-3.5 h-3.5 mr-1" /> Disconnect
              </Button>
            </>
          )}
        </div>

        {/* Progress bar */}
        {progress && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-[10px] text-muted-foreground">
              <span>{progress.label}</span>
              <span>{progress.current}/{progress.total}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="bg-[#0A66C2] h-1.5 rounded-full orka-transition-colors duration-500"
                style={{ width: `${Math.min(100, (progress.current / progress.total) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Live activity panel */}
        {isActive && (
          <div className="border rounded-lg overflow-hidden bg-card">
            {/* Inline Screenshot (small) */}
            {screenshot && !expanded && (
              <div className="border-b p-1">
                <div className="flex items-center justify-between text-[9px] text-muted-foreground mb-1 px-1">
                  <span className="flex items-center gap-1">
                    <MonitorPlay className="w-3 h-3" /> Live View{connecting ? ' — Click to interact' : ''}
                  </span>
                  {connecting && (
                    <button
                      onClick={() => { setExpanded(true); setTimeout(() => previewRef.current?.focus(), 50); }}
                      className="flex items-center gap-0.5 text-[#0A66C2] hover:underline"
                      data-testid="linkedin-expand-btn"
                    >
                      <Maximize2 className="w-3 h-3" /> Expand
                    </button>
                  )}
                </div>
                <div
                  ref={!expanded ? previewRef : undefined}
                  tabIndex={connecting ? 0 : -1}
                  onKeyDown={connecting ? handlePreviewKeyDown : undefined}
                  className="relative outline-none focus:ring-2 focus:ring-[#0A66C2] rounded"
                  data-testid="linkedin-preview-interactive"
                >
                  <img
                    ref={!expanded ? imgRef : undefined}
                    src={screenshot}
                    alt="LinkedIn browser"
                    className={`w-full rounded select-none ${connecting ? 'cursor-pointer' : ''}`}
                    onClick={connecting ? handlePreviewClick : undefined}
                    draggable={false}
                    data-testid="linkedin-preview-image"
                  />
                </div>
              </div>
            )}
            {/* Logs */}
            {!expanded && (
              <ScrollArea className="h-28 p-2">
                <div className="space-y-0.5">
                  {logs.map((log, i) => (
                    <p key={i} className="text-[10px] text-muted-foreground font-mono leading-tight">{log.text}</p>
                  ))}
                  <div ref={logEndRef} />
                </div>
              </ScrollArea>
            )}
          </div>
        )}

        {/* Expanded lightbox */}
        {expanded && screenshot && connecting && (
          <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-6" data-testid="linkedin-lightbox">
            <div className="relative w-full max-w-5xl flex flex-col gap-2">
              {/* Top bar */}
              <div className="flex items-center justify-between text-white text-xs">
                <span className="flex items-center gap-1.5 opacity-80">
                  <MonitorPlay className="w-4 h-4" /> LinkedIn Login — Click fields to interact, then type
                </span>
                <button
                  onClick={() => { setExpanded(false); setTimeout(() => previewRef.current?.focus(), 50); }}
                  className="flex items-center gap-1 bg-white/10 hover:bg-white/20 rounded px-2 py-1 transition-colors"
                  data-testid="linkedin-collapse-btn"
                >
                  <Minimize2 className="w-3.5 h-3.5" /> Collapse
                </button>
              </div>
              {/* Large interactive preview */}
              <div
                ref={previewRef}
                tabIndex={0}
                onKeyDown={handlePreviewKeyDown}
                className="relative outline-none focus:ring-2 focus:ring-[#0A66C2] rounded-lg overflow-hidden"
                data-testid="linkedin-preview-expanded"
              >
                <img
                  ref={imgRef}
                  src={screenshot}
                  alt="LinkedIn browser"
                  className="w-full rounded-lg select-none cursor-pointer"
                  onClick={handlePreviewClick}
                  draggable={false}
                />
              </div>
              {/* Compact log strip */}
              <ScrollArea className="h-20 bg-black/60 rounded-lg p-2">
                <div className="space-y-0.5">
                  {logs.map((log, i) => (
                    <p key={i} className="text-[10px] text-white/60 font-mono leading-tight">{log.text}</p>
                  ))}
                  <div ref={logEndRef} />
                </div>
              </ScrollArea>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
